import { Controller } from "@hotwired/stimulus"

// Admin sidebar group expand/collapse + persistence (GR-2 / #171).
//
// Wires on the <nav> root. Each group carries:
//   data-admin-sidebar-target="group"
//   data-group-key="<slug>"
//   data-expanded="true|false"
//
// The visual expand/collapse is CSS-only (grid-template-rows: 1fr|0fr on
// `.admin-sidebar__submenu` reacts to `[data-expanded]` on the parent
// group). This controller just flips `data-expanded` on click and mirrors
// `aria-expanded` on the toggle button.
//
// Persistence:
//   - Storage key:  "opinaki.admin.sidebar.expanded"
//   - Value:        JSON Array<string> of expanded group keys.
//   - On connect:   merges server-rendered state (groups already marked
//                   expanded because they contain the active route) with
//                   any prior user toggles. The active group always wins
//                   (forced expanded) so the user sees their current page.
//   - On viewport < 768px (mobile drawer):  we skip persistence so the
//     drawer always opens minimal — only the active group expanded. This
//     matches the issue criteria ("Mobile: sempre abre com tudo collapsed
//     exceto rota ativa, sem persistir").
const STORAGE_KEY = "opinaki.admin.sidebar.expanded"
const MOBILE_BREAKPOINT = 768

export default class extends Controller {
  static targets = ["group", "submenu", "item"]

  connect() {
    this.restoreState()
  }

  // Toggle invoked from group header / nested-item header click.
  toggle(event) {
    event.preventDefault()
    const groupEl = event.currentTarget.closest("[data-admin-sidebar-target='group']")
    if (!groupEl) return
    const isExpanded = groupEl.dataset.expanded === "true"
    this.setGroupExpanded(groupEl, !isExpanded)
    this.persistState()
  }

  setGroupExpanded(groupEl, expanded) {
    groupEl.dataset.expanded = expanded ? "true" : "false"
    const toggleBtn = groupEl.querySelector(":scope > button[aria-expanded]")
    if (toggleBtn) {
      toggleBtn.setAttribute("aria-expanded", expanded ? "true" : "false")
      // GR-8 (#177): keep the aria-label in sync with state. The server
      // renders an "Expand <group>" / "Collapse <group>" string; we swap
      // the verb here so SR users always hear the next action, not the
      // current one. Source strings live on the button as data attrs so
      // the JS stays i18n-aware without bundling translations.
      const expandLabel = toggleBtn.dataset.labelExpand
      const collapseLabel = toggleBtn.dataset.labelCollapse
      if (expandLabel && collapseLabel) {
        toggleBtn.setAttribute("aria-label", expanded ? collapseLabel : expandLabel)
      }
    }
  }

  restoreState() {
    // On mobile the drawer should always open minimal (only active route
    // group expanded). Server-rendered state already reflects that, so we
    // simply skip overlay restoration.
    if (this._isMobile()) return

    let saved = []
    try {
      saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "[]")
      if (!Array.isArray(saved)) saved = []
    } catch (_e) {
      saved = []
    }

    this.groupTargets.forEach((groupEl) => {
      const hasActive = groupEl.querySelector("[aria-current='page']") !== null
      const key = groupEl.dataset.groupKey
      const shouldExpand = hasActive || saved.includes(key)
      this.setGroupExpanded(groupEl, shouldExpand)
    })
  }

  persistState() {
    if (this._isMobile()) return
    const expandedKeys = this.groupTargets
      .filter((g) => g.dataset.expanded === "true")
      .map((g) => g.dataset.groupKey)
      .filter((k) => typeof k === "string" && k.length > 0)
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(expandedKeys))
    } catch (_e) {
      // localStorage may throw in private browsing or when quota is
      // exhausted — failing silently is fine; next reload just won't
      // restore the user's toggles.
    }
  }

  _isMobile() {
    return typeof window !== "undefined" && window.innerWidth < MOBILE_BREAKPOINT
  }
}
